import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faBasketball,
  faCakeCandles,
  faClover,
  faFlagUsa,
  faFlowerDaffodil,
  faGift,
  faGuitar,
  faHeart,
  faJackOLantern,
  faPartyHorn,
  faPumpkin,
  faSearch,
  faSnowflake,
  faSun,
  faTurkey,
} from '@fortawesome/pro-solid-svg-icons';
import { Action } from 'corso-admin-ui';
import { addDays, subDays } from 'date-fns';
import type { ReactNode } from 'react';

const semanticPriority = {
  low: 10,
  medium: 100,
  high: 1000,
} as const;

export type OccasionIcon = {
  name: string;
  icon: IconDefinition; // potentially a set of icons to randomly choose from for more variety
  priority: number;
  // ? may want to make this contextual by being locale-aware in the future
  match: (date: Date) => boolean;
  emphasize?: 'shimmer';
  /** Generally kept to prose. */
  content?: ReactNode;
};

export const getMonthDay = (date: Date) =>
  `${date.getMonth() + 1}-${date.getDate()}` as const;

export const isFixedDate =
  (fixed: { month: number; day: number }) => (date: Date) =>
    getMonthDay(date) === `${fixed.month}-${fixed.day}`;

export const isNthWeekdayOfMonth =
  ({
    month,
    weekday,
    nth,
  }: {
    month: number;
    /** 0 = Sunday, 1 = Monday, ..., 6 = Saturday */
    weekday: number;
    nth: number;
  }) =>
  (date: Date) => {
    if (date.getMonth() + 1 !== month) return false;
    if (date.getDay() !== weekday) return false;

    const firstDayOfMonth = new Date(date.getFullYear(), month - 1, 1).getDay();
    const firstOccurrence = 1 + ((weekday - firstDayOfMonth + 7) % 7);
    const expectedDate = firstOccurrence + (nth - 1) * 7;

    return date.getDate() === expectedDate;
  };

/** Date range inclusive, and will handle wrap around through the end of the year, if the end is before the start. */
export const isDateRange =
  (
    start: { month: number; day: number },
    end: { month: number; day: number },
  ) =>
  (date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const startDate = new Date(d.getFullYear(), start.month - 1, start.day);
    const endDate = new Date(d.getFullYear(), end.month - 1, end.day);
    if (endDate < startDate) return d >= startDate || d <= endDate; // wrap around end of year; i.e. December into January
    return d >= startDate && d <= endDate;
  };

/** Effectively a date range around a fixed date, inclusive of the `n` days before and after the fixed date. */
export const isAroundFixedDate =
  (fixed: { month: number; day: number }, n: number) => (date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const candidateYears = [
      d.getFullYear() - 1,
      d.getFullYear(),
      d.getFullYear() + 1,
    ];

    return candidateYears.some((year) => {
      const fixedDate = new Date(year, fixed.month - 1, fixed.day);
      const startDate = subDays(fixedDate, n);
      const endDate = addDays(fixedDate, n);
      return d >= startDate && d <= endDate;
    });
  };

const occasionIcons: OccasionIcon[] = [
  // seasons (approximate a quarterly range)
  {
    name: 'Spring',
    icon: faFlowerDaffodil,
    priority: semanticPriority.low,
    match: isDateRange({ month: 3, day: 20 }, { month: 6, day: 20 }),
  },
  {
    name: 'Summer',
    icon: faSun,
    priority: semanticPriority.low,
    match: isDateRange({ month: 6, day: 21 }, { month: 9, day: 22 }),
  },
  {
    name: 'Fall',
    icon: faPumpkin,
    priority: semanticPriority.low,
    match: isDateRange({ month: 9, day: 23 }, { month: 12, day: 20 }),
  },
  {
    name: 'Winter',
    icon: faSnowflake, // alternatively faSnowmanHead
    priority: semanticPriority.low,
    match: isDateRange({ month: 12, day: 21 }, { month: 3, day: 19 }),
  },

  // holidays
  {
    name: 'New Year',
    icon: faPartyHorn, // alternatively faChampagneGlasses
    priority: semanticPriority.medium,
    match: isAroundFixedDate({ month: 1, day: 1 }, 3),
  },
  {
    name: `Valentine's Day`,
    icon: faHeart,
    priority: semanticPriority.medium,
    match: isAroundFixedDate({ month: 2, day: 14 }, 3),
  },
  {
    name: `St. Patrick's Day`,
    icon: faClover, // alternatively faRainbow
    priority: semanticPriority.medium,
    match: isAroundFixedDate({ month: 3, day: 17 }, 3), // * earlier than events, so that this should still show in March
  },
  {
    name: 'Independence Day',
    icon: faFlagUsa,
    priority: semanticPriority.medium,
    match: isAroundFixedDate({ month: 7, day: 4 }, 3),
  },
  {
    name: 'Halloween',
    icon: faJackOLantern,
    priority: semanticPriority.medium,
    match: isAroundFixedDate({ month: 10, day: 31 }, 3),
  },
  {
    name: 'Thanksgiving',
    icon: faTurkey,
    priority: semanticPriority.medium,
    match: isNthWeekdayOfMonth({ month: 11, weekday: 4, nth: 4 }), // 4th Thursday of November
  },
  {
    name: 'Christmas',
    icon: faGift, // alternatively faTreeChristmas
    priority: semanticPriority.medium,
    match: isAroundFixedDate({ month: 12, day: 25 }, 3),
  },

  // events
  {
    name: 'Corso Anniversary',
    icon: faCakeCandles, // alternatively faFaceParty
    priority: semanticPriority.high,
    match: isFixedDate({ month: 7, day: 20 }), // IIRC Nate or someone said Corso was founded sometime around July 20th, 2021
    emphasize: 'shimmer',
    content: (
      <p className="max-w-prose text-pretty">
        Celebrate the anniversary marking the founding of Corso with us today!
      </p>
    ),
  },
  {
    name: 'March Madness',
    icon: faBasketball,
    priority: semanticPriority.medium,
    match: isDateRange({ month: 3, day: 1 }, { month: 3, day: 31 }), // all of March
  },
  {
    name: `Dolly Parton's Birthday`,
    icon: faGuitar,
    priority: semanticPriority.high,
    match: isFixedDate({ month: 1, day: 19 }), // Dolly Parton's birthday is January 19th, 1946
    emphasize: 'shimmer',
    content: (
      <div className="max-w-prose text-pretty">
        <blockquote>
          &ldquo;A rhinestone shines just as good as a diamond.&rdquo;
        </blockquote>
        <cite>
          <Action
            variant="link"
            to="https://www.youtube.com/watch?v=UbxUSsFXYo4"
          >
            Dolly Parton
          </Action>
        </cite>
      </div>
    ),
  },
];

const fallback: OccasionIcon = {
  name: 'Default',
  icon: faSearch,
  priority: -Infinity,
  match: () => true,
};

export function getQuickSearchIcon(date = new Date()) {
  occasionIcons.sort((a, b) => b.priority - a.priority);
  // just need the first match after sorting for highest priority
  const best = occasionIcons.find((occasionIcon) => occasionIcon.match(date));
  return best ?? fallback;
}
